/**
 * Alpine.js component for @mention links in activity notes.
 * Intercepts clicks on [data-mention-link] anchors, fetches the team
 * member profile card via AJAX, and displays it in a modal.
 */
const TEAM_PROFILE_PATH = /^\/team\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function teamProfilePath(href, origin = window.location.origin) {
  if (!href) return null;
  let url;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || !TEAM_PROFILE_PATH.test(url.pathname)) return null;
  return url.pathname;
}

export default function registerMentionLink(Alpine) {
  Alpine.data("mentionLink", () => ({
    open: false,
    previouslyFocused: null,

    init() {
      // Use event delegation on the document to catch mention link clicks
      this._onClick = (e) => {
        const link = e.target.closest("a[data-mention-link]");
        if (!link) return;

        // The fetched HTML is injected with innerHTML, so only same-origin
        // team profile URLs are ever fetched; anything else is left to the
        // browser as a normal link.
        const path = teamProfilePath(link.getAttribute("href"));
        if (!path) return;

        e.preventDefault();
        this.loadProfile(path);
      };
      document.addEventListener("click", this._onClick);

      this._onKeydown = (e) => {
        if (!this.open) return;
        if (e.key === "Tab") this.trapFocus(e);
      };
      document.addEventListener("keydown", this._onKeydown);
    },

    destroy() {
      document.removeEventListener("click", this._onClick);
      document.removeEventListener("keydown", this._onKeydown);
    },

    _focusables() {
      const container = this.$refs.profileDialog;
      if (!container) return [];
      return Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
    },

    trapFocus(event) {
      const focusables = this._focusables();
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },

    async loadProfile(url) {
      this.previouslyFocused = document.activeElement;
      this.open = true;

      // Reset to loading spinner
      const container = this.$refs.profileContent;
      container.innerHTML =
        '<div class="flex items-center justify-center py-8">' +
        '<svg class="h-6 w-6 animate-spin text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">' +
        '<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>' +
        '<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>' +
        "</svg></div>";

      try {
        const response = await fetch(url, {
          headers: {
            "X-Profile-Modal": "true",
            Accept: "text/html"
          }
        });

        if (response.ok) {
          container.innerHTML = await response.text();
          this.$nextTick(() => {
            const focusables = this._focusables();
            if (focusables.length > 0) focusables[0].focus();
          });
        } else {
          // Fallback: navigate to the full profile page
          window.location.href = url;
        }
      } catch {
        window.location.href = url;
      }
    },

    close() {
      this.open = false;
      if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
        this.previouslyFocused.focus();
      }
      this.previouslyFocused = null;
    }
  }));
}
